import React, { useEffect, useRef } from 'react';
import { View } from '@instructure/ui-view';
import { CloseButton } from '@instructure/ui-buttons';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { canvas } from '@instructure/ui-themes';
const Modal = ({
  open,
  onDismiss,
  label,
  children,
  mountNode,
  size = 'medium',
  shouldCloseOnDocumentClick = true,
  shouldCloseOnEscape = true,
  shouldFocusOnOpen = true,
  shouldReturnFocus = true,
  closeButtonLabel = 'Close',
  ...props
}) => {
  const backdropRef = useRef(null);
  const modalRef = useRef(null);
  const previouslyFocusedElement = useRef(null);

  useEffect(() => {
    if (open && shouldFocusOnOpen) {
      previouslyFocusedElement.current = document.activeElement;
      modalRef.current?.focus();
    }

    return () => {
      if (shouldReturnFocus && previouslyFocusedElement.current?.focus) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, [open, shouldFocusOnOpen, shouldReturnFocus]);

  useEffect(() => {
    if (!open || !shouldCloseOnEscape) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onDismiss?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, shouldCloseOnEscape, onDismiss]);

  const handleClickOutside = (e) => {
    if (
      shouldCloseOnDocumentClick &&
      backdropRef.current &&
      e.target === backdropRef.current
    ) {
      onDismiss?.();
    }
  };

  if (!open || !mountNode) return null;

  const sizeStyles = {
    small: '30rem',
    medium: '40rem',
    large: '50rem',
    auto: 'auto',
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleClickOutside}
      className="Overlay Modal__overlay"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483647,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          minHeight: '100%',
          padding: '3rem 1.5rem',
          boxSizing: 'border-box',
        }}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          className="Modal__content"
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: sizeStyles[size] || sizeStyles.medium,
            backgroundColor: '#fff',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
            padding: 0,
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '0.5rem',
          }}
          {...props}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

Modal.Header = ({ children }) => (
  <View
    as="div"
    className="Modal__header"
    padding="medium"
    background={canvas.colors.contrasts.white1010}
    borderWidth="0 0 small 0"
    borderColor={canvas.colors.contrasts.grey1214}
    borderRadius="medium medium 0 0"
  >
    {children}
  </View>
);

Modal.Body = ({ children }) => (
  <View as="div" className="Modal__body" padding="medium" flex="1 1 auto">
    {children}
  </View>
);

Modal.Footer = ({ children }) => (
  <View
    as="div"
    className="Modal__footer"
    padding="small"
    background="secondary"
    borderWidth="small 0 0 0"
    borderColor={canvas.colors.contrasts.grey1214}
    borderRadius="0 0 medium medium"
    textAlign="end"
  >
    {children}
  </View>
);

Modal.CloseButton = ({ onClick, label = 'Close' }) => (
  <CloseButton
    placement="end"
    offset="small"
    screenReaderLabel={label}
    onClick={onClick}
  />
);

export default Modal;
